import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
} from "reactstrap"
import {
  createVinculo,
  getVinculosByEmpresa,
  deleteVinculo,
} from "helpers/vinculo_helper"
import { getParceiros } from "helpers/parceiro_helper"
import { FaTrash, FaPlus, FaLink } from "react-icons/fa"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

const VinculosManagement = ({ empresaId, empresaNome }) => {
  const [vinculos, setVinculos] = useState([])
  const [parceiros, setParceiros] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [novoVinculo, setNovoVinculo] = useState({ Id_Parceiro: "" })

  useEffect(() => {
    if (empresaId) {
      carregarDados()
    }
  }, [empresaId])

  const carregarDados = async () => {
    setLoading(true)
    try {
      const [vinculosData, parceirosData] = await Promise.all([
        getVinculosByEmpresa(empresaId),
        getParceiros(),
      ])

      setVinculos(vinculosData || [])
      setParceiros(parceirosData.filter(p => !p.DataExclusao) || [])
    } catch (error) {
      console.error("Erro ao carregar dados:", error)
      toastr.error("Erro ao carregar dados dos vínculos", "Erro")
    } finally {
      setLoading(false)
    }
  }

  const toggleModal = () => {
    setModalOpen(!modalOpen)
    if (!modalOpen) {
      setNovoVinculo({ Id_Parceiro: "" })
    }
  }

  const handleCriarVinculo = async () => {
    if (!novoVinculo.Id_Parceiro) {
      toastr.warning("Selecione um Parceiro", "Atenção")
      return
    }

    setLoading(true)
    try {
      await createVinculo(empresaId, novoVinculo.Id_Parceiro)
      toastr.success("Vínculo criado com sucesso!", "Sucesso")
      setNovoVinculo({ Id_Parceiro: "" })
      toggleModal()
      carregarDados()
    } catch (error) {
      console.error("Erro ao criar vínculo:", error)
      if (error.response?.status === 400) {
        toastr.error(error.response.data.message || "Dados inválidos", "Erro")
      } else {
        toastr.error("Erro ao criar vínculo", "Erro")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleRemoverVinculo = async vinculoId => {
    if (window.confirm("Deseja realmente remover este vínculo?")) {
      setLoading(true)
      try {
        await deleteVinculo(vinculoId)
        toastr.success("Vínculo removido com sucesso!", "Sucesso")
        carregarDados()
      } catch (error) {
        console.error("Erro ao remover vínculo:", error)
        toastr.error("Erro ao remover vínculo", "Erro")
      } finally {
        setLoading(false)
      }
    }
  }

  const seguradoras = parceiros.filter(p => p.Tipo === "seguradora")
  const corretoras = parceiros.filter(p => p.Tipo === "corretora")

  return (
    <>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="card-title mb-0">
              <FaLink className="me-2" />
              Vínculos de Parceiros
            </h4>
            <Button color="primary" onClick={toggleModal} disabled={loading}>
              <FaPlus className="me-1" />
              Adicionar Vínculo
            </Button>
          </div>

          {empresaNome && (
            <div className="alert alert-info" role="alert">
              <strong>Empresa:</strong> {empresaNome}
            </div>
          )}

          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Carregando...</span>
              </div>
            </div>
          ) : vinculos.length === 0 ? (
            <div className="text-center text-muted py-4">
              <p>Nenhum vínculo cadastrado para esta empresa</p>
              <p className="small">
                Clique em "Adicionar Vínculo" para criar o primeiro vínculo
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table table-striped table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "40%" }}>Parceiro</th>
                    <th style={{ width: "30%" }}>Tipo</th>
                    <th style={{ width: "15%" }} className="text-center">
                      Status
                    </th>
                    <th style={{ width: "15%" }} className="text-center">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vinculos.map(v => (
                    <tr key={v.Id}>
                      <td>
                        {v.Parceiro ? (
                          <span className="badge bg-primary">
                            {v.Parceiro.Nome}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {v.Parceiro?.Tipo === "seguradora" ? "Seguradora" : v.Parceiro?.Tipo === "corretora" ? "Corretora" : "-"}
                      </td>
                      <td className="text-center">
                        {v.Status ? (
                          <span className="badge bg-success">Ativo</span>
                        ) : (
                          <span className="badge bg-secondary">Inativo</span>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleRemoverVinculo(v.Id)}
                          title="Remover vínculo"
                          disabled={loading}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Adicionar Novo Vínculo</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label for="parceiro">Parceiro</Label>
            <Input
              type="select"
              id="parceiro"
              value={novoVinculo.Id_Parceiro}
              onChange={e =>
                setNovoVinculo({ Id_Parceiro: e.target.value })
              }
            >
              <option value="">Selecione um Parceiro</option>
              {seguradoras.length > 0 && (
                <optgroup label="Seguradoras">
                  {seguradoras.map(p => (
                    <option key={p.Id} value={p.Id}>
                      {p.Nome}
                    </option>
                  ))}
                </optgroup>
              )}
              {corretoras.length > 0 && (
                <optgroup label="Corretoras">
                  {corretoras.map(p => (
                    <option key={p.Id} value={p.Id}>
                      {p.Nome}
                    </option>
                  ))}
                </optgroup>
              )}
            </Input>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleCriarVinculo}
            disabled={loading}
          >
            {loading ? "Salvando..." : "Adicionar"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default VinculosManagement
